import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { applyForumEvent, forumApi, MAX_FORUM_TAGS, MAX_TOPIC_TAGS, selectedForumTags } from '@/lib/forums'
import { describeError } from '@/lib/auth'
import type { Channel, ForumTag } from '@/types/api'
import { Modal } from '../settings/Modal'
import { cx } from '@/lib/cx'
import styles from './ForumChannel.module.css'

export function ForumTagPicker({ tags, selected, onChange, disabled = false }: { tags: ForumTag[]; selected: string[]; onChange: (ids: string[]) => void; disabled?: boolean }) {
  const ids = selectedForumTags(tags, selected)
  return <div className={styles.tagPicker} role="group" aria-label="Теги темы">
    {tags.map(tag => <button type="button" key={tag.id} aria-pressed={ids.includes(tag.id)} disabled={disabled || !ids.includes(tag.id) && ids.length >= MAX_TOPIC_TAGS} className={cx(styles.tag, ids.includes(tag.id) && styles.tagSelected)} onClick={() => onChange(ids.includes(tag.id) ? ids.filter(id => id !== tag.id) : [...ids, tag.id])}>{tag.name}</button>)}
  </div>
}

export function ForumTagSettings({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const client = useQueryClient()
  const [tags, setTags] = useState(() => (channel.available_tags ?? []).map(tag => ({ ...tag, key: tag.id })))
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    const userId = useAuthStore.getState().user?.id
    if (busyRef.current || !userId) return
    busyRef.current = true; setBusy(true); setError(null)
    try {
      const next = await forumApi.setTags(channel.id, tags.map(tag => ({ ...(tag.id ? { id: tag.id } : {}), name: tag.name.trim() })))
      if (useAuthStore.getState().user?.id !== userId) return
      useGuildStore.getState().applyEvent('CHANNEL_UPDATE', next)
      applyForumEvent(client, userId, 'CHANNEL_UPDATE', next)
      onClose()
    } catch (err) { setError(describeError(err).message) }
    finally { busyRef.current = false; setBusy(false) }
  }
  const duplicate = tags.some((tag, i) => tags.some((other, j) => j < i && other.name.trim().toLocaleLowerCase() === tag.name.trim().toLocaleLowerCase()))
  return <Modal title="Теги форума" width={520} onClose={() => { if (!busyRef.current) onClose() }}>
    <form className={styles.form} onSubmit={event => { event.preventDefault(); void save() }}>
      <p className={styles.inForum}>До {MAX_FORUM_TAGS} тегов, до 20 символов в названии</p>
      <div className={styles.tagRows}>{tags.map((tag, i) => <div key={tag.key} className={styles.tagRow}>
        <input aria-label={`Название тега ${i + 1}`} value={tag.name} maxLength={20} disabled={busy} placeholder="Название тега" onChange={event => setTags(prev => prev.map(item => item.key === tag.key ? { ...item, name: event.target.value } : item))} />
        <button type="button" disabled={busy} aria-label={`Убрать тег ${tag.name || i + 1}`} onClick={() => setTags(prev => prev.filter(item => item.key !== tag.key))}><X size={18} /></button>
      </div>)}</div>
      {tags.length < MAX_FORUM_TAGS ? <button type="button" className={styles.addFile} disabled={busy} onClick={() => setTags(prev => [...prev, { id: '', name: '', key: crypto.randomUUID() }])}><Plus size={16} />Добавить тег</button> : null}
      {duplicate ? <p role="alert" className={styles.error}>Названия тегов должны отличаться</p> : null}
      {error ? <p role="alert" className={styles.error}>{error}</p> : null}
      <div className={styles.formActions}><button type="button" className={styles.cancel} disabled={busy} onClick={onClose}>Отмена</button><button type="submit" className={styles.create} disabled={busy || duplicate || tags.some(tag => !tag.name.trim())}>{busy ? 'Сохраняем…' : 'Сохранить'}</button></div>
    </form>
  </Modal>
}

export function ForumTopicTags({ forumId, thread, tags, onClose }: { forumId: string; thread: Channel; tags: ForumTag[]; onClose: () => void }) {
  const client = useQueryClient()
  const [selected, setSelected] = useState(thread.applied_tags ?? [])
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    const userId = useAuthStore.getState().user?.id
    if (busyRef.current || !userId) return
    busyRef.current = true; setBusy(true); setError(null)
    try {
      const next = await forumApi.update(forumId, thread.id, { applied_tags: selectedForumTags(tags, selected) })
      if (useAuthStore.getState().user?.id !== userId) return
      useGuildStore.getState().applyEvent('THREAD_UPDATE', next)
      applyForumEvent(client, userId, 'THREAD_UPDATE', next)
      onClose()
    } catch (err) { setError(describeError(err).message) }
    finally { busyRef.current = false; setBusy(false) }
  }
  return <Modal title="Теги темы" onClose={() => { if (!busyRef.current) onClose() }}>
    <form className={styles.form} onSubmit={event => { event.preventDefault(); void save() }}>
      <p className={styles.inForum}>{thread.name}</p>
      <ForumTagPicker tags={tags} selected={selected} onChange={setSelected} disabled={busy} />
      <p className={styles.inForum}>До {MAX_TOPIC_TAGS} тегов</p>
      {error ? <p role="alert" className={styles.error}>{error}</p> : null}
      <div className={styles.formActions}><button type="button" className={styles.cancel} disabled={busy} onClick={onClose}>Отмена</button><button type="submit" className={styles.create} disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button></div>
    </form>
  </Modal>
}
